/**
 * @file PageData.tsx
 * @module _admin/layout/core/PageData
 * @description Provides the PageData layout-core component, configuration, or context used to initialize and manage UTrade Admin layout state.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  FC,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { WithChildren } from "../../helpers";
import { getKey, setKey } from "../../../Global/history";

export interface PageLink {
  title: string;
  path: string;
  isActive: boolean;
  isSeparator?: boolean;
}

export interface PageDataContextModel {
  pageTitle?: string;
  setPageTitle: (_title: string) => void;
  pageDescription?: string;
  setPageDescription: (_description: string) => void;
  pageBreadcrumbs?: Array<PageLink>;
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
  previousPageTitle?: string;
  previousPagePath?: string;
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
});

const PageDataProvider: FC<WithChildren> = ({ children }) => {
  const [pageTitle, setPageTitle] = useState<string>("");
  const [pageDescription, setPageDescription] = useState<string>("");
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
  const [previousPageTitle, setPreviousPageTitle] = useState<string | undefined>();
  const [previousPagePath, setPreviousPagePath] = useState<string | undefined>();

  const location = useLocation();
  const lastPathRef = useRef<string>(location.pathname);
  const lastTitleRef = useRef<string>("");

  // On first load, restore the previously stored "previous page" title
  // so that a full page refresh keeps the same back label.
  useEffect(() => {
    const storedTitle = getKey("previousPageTitle");
    const storedPath = getKey("previousPagePath");
    if (typeof storedTitle === "string" && storedTitle) {
      setPreviousPageTitle(storedTitle);
    }
    if (typeof storedPath === "string" && storedPath) {
      setPreviousPagePath(storedPath);
    }
  }, []);

  useEffect(() => {
    // When the route path changes, freeze the title of the
    // page we are leaving as the "previous" page title.
    if (location.pathname !== lastPathRef.current) {
      if (lastTitleRef.current) {
        setPreviousPageTitle(lastTitleRef.current);
        setPreviousPagePath(lastPathRef.current);
        setKey("previousPageTitle", lastTitleRef.current, false);
        setKey("previousPagePath", lastPathRef.current, false);
      }
      lastPathRef.current = location.pathname;
    }

    // Always keep track of the latest non-empty title so it can
    // be used on the next navigation as the previous page title.
    if (pageTitle) {
      lastTitleRef.current = pageTitle;
    }
  }, [location.pathname, pageTitle]);

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    previousPageTitle,
    previousPagePath,
  };
  return (
    <PageDataContext.Provider value={value}>
      {children}
    </PageDataContext.Provider>
  );
};

function usePageData() {
  return useContext(PageDataContext);
}

type Props = {
  description?: string;
  breadcrumbs?: Array<PageLink>;
};

const PageTitle = ({
  children,
  description,
  breadcrumbs,
}: Props & WithChildren): null => {
  const { setPageTitle, setPageDescription, setPageBreadcrumbs } =
    usePageData();
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString());
    }
    return () => {
      setPageTitle("");
    };
  }, [children]);

  useEffect(() => {
    if (description) {
      setPageDescription(description);
    }
    return () => {
      setPageDescription("");
    };
  }, [description]);

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs);
    }
    return () => {
      setPageBreadcrumbs([]);
    };
  }, [breadcrumbs]);

  return null;
};

const PageDescription = ({ children }: WithChildren): null => {
  const { setPageDescription } = usePageData();
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString());
    }
    return () => {
      setPageDescription("");
    };
  }, [children]);
  return null;
};

export { PageDescription, PageTitle, PageDataProvider, usePageData };
